"use client";

import { Bot, Send, User, X } from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useEffect, useRef, useState } from "react";
import { siteConfig } from "../lib/site-config";
import { useImCustomerChat } from "./use-im-customer-chat";

export function ImCustomerChat({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [input, setInput] = useState("");
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const { connected, messages, sendMessage } = useImCustomerChat(open);

  useEffect(() => {
    if (open) messagesEndRef.current?.scrollIntoView({ block: "end", behavior: "smooth" });
  }, [messages, open]);

  useEffect(() => {
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    if (open) window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onClose, open]);

  const handleSend = async () => {
    if (!input.trim()) return;
    const sent = await sendMessage(input, window.location.pathname);
    if (sent) setInput("");
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="im-customer-chat-overlay"
          role="presentation"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.14 }}
          onMouseDown={onClose}
        >
          <motion.section
            className="im-customer-chat"
            role="dialog"
            aria-modal="true"
            aria-labelledby="im-customer-chat-title"
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 6 }}
            transition={{ duration: 0.18, ease: [0.22, 1, 0.36, 1] }}
            onMouseDown={(event) => event.stopPropagation()}
          >
            <header className="im-customer-chat-header">
              <span className="im-customer-chat-mark"><Bot size={19} /></span>
              <div>
                <h2 id="im-customer-chat-title">{siteConfig.chat.title}</h2>
                <span>{connected ? `${siteConfig.chat.serviceName}在线` : "正在连接客服"}</span>
              </div>
            </header>
            <button className="im-customer-chat-close" type="button" onClick={onClose} aria-label="关闭在线咨询" title="关闭">
              <X size={19} />
            </button>
            <div className="im-customer-chat-messages" aria-live="polite">
              {messages.length === 0 && (
                <p className="im-customer-chat-empty">{connected ? "请留下您的问题，增长顾问会尽快回复。" : "正在建立与增长顾问的连接。"}</p>
              )}
              {messages.map((message) => (
                <div className={`im-customer-chat-message is-${message.sender}`} key={message.id}>
                  <span className="im-customer-chat-avatar">
                    {message.sender === "advisor" ? <Bot size={16} /> : <User size={16} />}
                  </span>
                  <div>
                    {message.sender === "advisor" && <span className="im-customer-chat-sender">{siteConfig.chat.serviceName}</span>}
                    <p>{message.content}</p>
                    <time>{message.timestamp.toLocaleTimeString("zh-CN", { hour: "2-digit", minute: "2-digit" })}</time>
                  </div>
                </div>
              ))}
              <div ref={messagesEndRef} />
            </div>
            <div className="im-customer-chat-composer">
              <textarea
                value={input}
                onChange={(event) => setInput(event.target.value)}
                onKeyDown={(event) => {
                  if (event.key === "Enter" && !event.shiftKey && !event.nativeEvent.isComposing) {
                    event.preventDefault();
                    void handleSend();
                  }
                }}
                placeholder={connected ? siteConfig.chat.placeholder : "正在连接客服"}
                aria-label={siteConfig.chat.placeholder}
                disabled={!connected}
              />
              <button type="button" onClick={() => void handleSend()} disabled={!connected || !input.trim()} aria-label="发送消息" title="发送消息">
                <Send size={19} />
              </button>
            </div>
          </motion.section>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
